/** Below 1100 px of content there is no dock column: the ledger is the bottom sheet, and
 *  the header's **Preview** button opens this right-side drawer with the same pane — the
 *  page's one render state, so opening and closing it never re-renders the phone (the
 *  tour plan §1 "Dock"). The drawer slides in over 225 ms; under reduced motion it appears at
 *  once. */
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { prefersReducedMotion } from '@/features/records/page/recordPage';

import { PREVIEW_DOCK_WIDTH } from './PreviewDock';

export interface PreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The record's preview pane (the dock's Preview body). */
  children: ReactNode;
  /** Back to the opener on close (default); off when the page closes it (the opener is gone). */
  restoreFocus?: boolean;
  /** After the slide out. */
  onExited?: () => void;
}

export function PreviewDrawer({ open, onClose, children, restoreFocus = true, onExited }: PreviewDrawerProps) {
  const titleId = useId();
  // The pane stays through the slide out and goes when it has ended.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the app bar (zIndex.drawer + 1): a temporary drawer at zIndex.drawer sits
      // under it, its title and Close hidden (lessons: "Overlays that must clear the AppBar").
      sx={{ zIndex: (t) => t.zIndex.modal }}
      transitionDuration={prefersReducedMotion() ? 0 : undefined}
      disableRestoreFocus={!restoreFocus}
      SlideProps={{
        onExited: () => {
          setMounted(false);
          onExited?.();
        },
      }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': titleId,
        // The dock's width: the phone is drawn at the size it has in the dock.
        sx: { width: PREVIEW_DOCK_WIDTH, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
      data-preview-drawer
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.75, pr: 0.75, py: 0.75, borderBottom: 1, borderColor: 'divider', flex: 'none' }}>
        <Typography id={titleId} variant="subtitle2" component="h2" sx={{ m: 0, flex: 1 }}>
          Preview
        </Typography>
        <Tooltip title="Close (Esc)">
          <IconButton size="small" onClick={onClose} aria-label="Close the preview">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 1.75, py: 1.25 }}>
        {(open || mounted) && children}
      </Box>
    </Drawer>
  );
}
